<template>
  <AkTooltipInformation
    v-if="shouldShowTooltip"
    :content="tooltipContent"
    :auto-hide="true"
    placement="bottom"
    trigger="hover"
    type="success"
    data-testid="credit-limit-pill-tooltip"
  >
    <a
      :href="pillHref"
      class="credit-limit-pill"
      data-testid="credit-limit-pill"
      @click="trackPillClick"
    >
      <AkBadge
        :content="badgeConfig.content"
        :color="badgeConfig.color"
        :size="AkBadgeSize.Xs"
        :symbol="badgeConfig.symbol"
      />
    </a>
  </AkTooltipInformation>
  <a
    v-else
    :href="pillHref"
    class="credit-limit-pill"
    data-testid="credit-limit-pill"
    @click="trackPillClick"
  >
    <AkBadge
      :content="badgeConfig.content"
      :color="badgeConfig.color"
      :size="AkBadgeSize.Xs"
      :symbol="badgeConfig.symbol"
    />
  </a>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import { AkBadge, AkBadgeColor, AkBadgeSize, AkTooltipInformation } from '@ankorstore/design-system';
import { useCreditLimit } from '@modules/account/retailer-settings/composables/use-credit-limit';
import { useMediaQuery } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { UserClick } from '@ankorstore/events-tracking';
import Analytics from '@monolith/legacy/services/analytics';
import { useRegistrationNumberLabel } from '@modules/account/retailer-settings/utils/registration-number-label';
import { CreditLimitPillViewedEvent } from '@monolith/legacy/services/analytics/events/aria-payment.event';
import { CreditLimitState } from '@modules/account/retailer-settings/composables/credit-limit/credit-limit-state';

enum PillState {
  Refused = 'refused',
  Frozen = 'frozen',
  Incomplete = 'incomplete',
  KycRequired = 'kyc_required',
  KycInReview = 'kyc_in_review',
  Active = 'active',
  // Ankorstore's own payment-terms verification (RCN-498) — distinct from the Aria KYC states above.
  BusinessVerificationRequired = 'business_verification_required',
  BusinessVerificationInReview = 'business_verification_in_review',
  BusinessVerificationEscalated = 'business_verification_escalated',
  BusinessVerificationRejected = 'business_verification_rejected',
}

// The required/in-review verification pills deep-link to the settings card that starts/resumes the
// flow; the Stripe redirect itself belongs to RCN-499 and is out of scope here. No element with
// this hash exists on the settings page yet — RCN-499 is expected to add it.
const BUSINESS_VERIFICATION_SETTINGS_HREF = '/account/settings#deferred-payment';
const DEFAULT_HREF = '/v2/account/checkouts';

interface Props {
  // This component mounts twice at once (desktop #header + mobile #headerMobile, CSS-toggled by
  // the xl breakpoint, not v-if) — each parent passes whether ITS instance is the visible one so
  // impressions aren't double-counted. Defaults true for any other/standalone usage.
  isVisible?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isVisible: true,
});

const { t } = useI18n();
const store = useStore();
const countryCode = computed(() => store.getters.retailer.business.country.iso_code);
const registrationNumberLabel = useRegistrationNumberLabel(countryCode);
const {
  formattedAvailableAmount,
  formattedUnpaidAmount,
  creditLimitIncomplete,
  creditLimitFrozen,
  creditLimitRefused,
  creditLimitKycRequired,
  creditLimitKycInReview,
  businessVerificationState,
  isBusinessVerificationPending,
  isLegalInformationProtectedRefusal,
  hasMultipleProviders,
  activeProviderCount,
  isAriaPsp,
  frozenReason,
  isFrozenReasonLoading,
} = useCreditLimit();
const isCompact = useMediaQuery('(max-width: 1439px)');

const shouldShowTooltip = computed(
  () =>
    businessVerificationState.value === CreditLimitState.BUSINESS_VERIFICATION_REQUIRED ||
    businessVerificationState.value === CreditLimitState.BUSINESS_VERIFICATION_IN_REVIEW ||
    businessVerificationState.value === CreditLimitState.BUSINESS_VERIFICATION_ESCALATED ||
    businessVerificationState.value === CreditLimitState.BUSINESS_VERIFICATION_REJECTED ||
    creditLimitRefused.value ||
    // Hidden during the frozen-reason fetch to avoid flashing the wrong (outstanding-balance)
    // message before we know whether this is actually a delayed order instead.
    (creditLimitFrozen.value && !isFrozenReasonLoading.value) ||
    creditLimitIncomplete.value ||
    hasMultipleProviders.value ||
    // KYC in-review has no tooltip yet (copy pending), so only the actionable required pill gets one.
    creditLimitKycRequired.value
);

const label = computed(() =>
  isCompact.value ? t('retailer.header.creditLimit.badge.mobile') : t('retailer.header.creditLimit.badge')
);

const tooltipContent = computed(() => {
  if (businessVerificationState.value === CreditLimitState.BUSINESS_VERIFICATION_REQUIRED) {
    return t('retailer.header.businessVerification.badge.required.tooltip');
  }
  if (businessVerificationState.value === CreditLimitState.BUSINESS_VERIFICATION_IN_REVIEW) {
    return t('retailer.header.businessVerification.badge.inReview.tooltip');
  }
  if (businessVerificationState.value === CreditLimitState.BUSINESS_VERIFICATION_ESCALATED) {
    return t('retailer.header.businessVerification.badge.escalated.tooltip');
  }
  if (businessVerificationState.value === CreditLimitState.BUSINESS_VERIFICATION_REJECTED) {
    return t('retailer.header.businessVerification.badge.rejected.tooltip');
  }
  if (creditLimitKycRequired.value) {
    return t('retailer.header.creditLimit.badge.kyc.required.tooltip');
  }
  if (isLegalInformationProtectedRefusal.value) {
    return t('retailer.header.creditLimit.badge.refused.legalInformation.tooltip');
  }
  if (creditLimitRefused.value) {
    return t('retailer.header.creditLimit.badge.refused.tooltip', { registrationNumber: registrationNumberLabel.value });
  }
  if (creditLimitFrozen.value) {
    if (frozenReason.value === 'both' && formattedUnpaidAmount.value) {
      return t('retailer.header.creditLimit.badge.frozen.tooltip.outstandingAndDelayed', {
        amount: formattedUnpaidAmount.value,
      });
    }
    // 'both' without an amount falls back to the delayed-only copy — the reason and the amount
    // come from different sources (Purchase API vs. the CMS pill) and can disagree, and a
    // combined message with a blank amount is worse than one that just omits the balance.
    if (frozenReason.value === 'delayed' || frozenReason.value === 'both') {
      return t('retailer.header.creditLimit.badge.frozen.tooltip.delayedOnly');
    }
    return formattedUnpaidAmount.value
      ? t('retailer.header.creditLimit.badge.frozen.tooltip.withAmount', { amount: formattedUnpaidAmount.value })
      : t('retailer.header.creditLimit.badge.frozen.tooltip');
  }
  if (creditLimitIncomplete.value) {
    const provider = isAriaPsp.value ? 'ARIA' : 'Ankorstore';
    return t('retailer.header.creditLimit.badge.tooltip', { provider });
  }
  if (hasMultipleProviders.value) {
    return t('retailer.header.creditLimit.badge.multiProvider.tooltip', { count: activeProviderCount.value });
  }
  return '';
});

const pillHref = computed(() => {
  if (
    businessVerificationState.value === CreditLimitState.BUSINESS_VERIFICATION_REQUIRED ||
    businessVerificationState.value === CreditLimitState.BUSINESS_VERIFICATION_IN_REVIEW
  ) {
    return BUSINESS_VERIFICATION_SETTINGS_HREF;
  }
  return DEFAULT_HREF;
});

const badgeConfig = computed(() => {
  // Takes precedence over every other pill below — see use-credit-limit-pill.ts.
  if (isBusinessVerificationPending.value) {
    if (businessVerificationState.value === CreditLimitState.BUSINESS_VERIFICATION_IN_REVIEW) {
      return { content: t('retailer.header.businessVerification.badge.inReview'), color: AkBadgeColor.Cold, symbol: 'clock' };
    }
    if (businessVerificationState.value === CreditLimitState.BUSINESS_VERIFICATION_ESCALATED) {
      return {
        content: t('retailer.header.businessVerification.badge.escalated'),
        color: AkBadgeColor.OrangeFull,
        symbol: 'exclamation-triangle',
      };
    }
    if (businessVerificationState.value === CreditLimitState.BUSINESS_VERIFICATION_REJECTED) {
      return {
        content: t('retailer.header.businessVerification.badge.rejected'),
        color: AkBadgeColor.RedFull,
        symbol: 'x-circle',
      };
    }
    // Required and expired render identically.
    return { content: t('retailer.header.businessVerification.badge.required'), color: AkBadgeColor.OrangeFull, symbol: 'lock' };
  }
  if (creditLimitRefused.value) {
    return { content: t('retailer.header.creditLimit.badge.refused'), color: AkBadgeColor.RedFull, symbol: 'lock' };
  }
  // KYC pills reuse the settings badges — same label and visual across surfaces.
  if (creditLimitKycRequired.value) {
    return { content: t('retailer.settings.creditLimit.kyc.required.badge'), color: AkBadgeColor.RedFull, symbol: 'lock' };
  }
  if (creditLimitKycInReview.value) {
    return { content: t('retailer.settings.creditLimit.kyc.pending.badge'), color: AkBadgeColor.Cold, symbol: 'clock' };
  }

  const content = `${label.value} ${formattedAvailableAmount.value}`;

  if (creditLimitFrozen.value) {
    return { content, color: AkBadgeColor.GreyFull, symbol: 'exclamation-triangle' };
  }
  if (creditLimitIncomplete.value) {
    return { content, color: AkBadgeColor.Warning700, symbol: 'lock' };
  }
  return { content, color: AkBadgeColor.Blue, symbol: null };
});

const pillState = computed(() => {
  if (businessVerificationState.value === CreditLimitState.BUSINESS_VERIFICATION_REQUIRED) {
    return PillState.BusinessVerificationRequired;
  }
  if (businessVerificationState.value === CreditLimitState.BUSINESS_VERIFICATION_IN_REVIEW) {
    return PillState.BusinessVerificationInReview;
  }
  if (businessVerificationState.value === CreditLimitState.BUSINESS_VERIFICATION_ESCALATED) {
    return PillState.BusinessVerificationEscalated;
  }
  if (businessVerificationState.value === CreditLimitState.BUSINESS_VERIFICATION_REJECTED) {
    return PillState.BusinessVerificationRejected;
  }
  if (creditLimitRefused.value) {
    return PillState.Refused;
  }
  if (creditLimitFrozen.value) {
    return PillState.Frozen;
  }
  if (creditLimitIncomplete.value) {
    return PillState.Incomplete;
  }
  if (creditLimitKycRequired.value) {
    return PillState.KycRequired;
  }
  if (creditLimitKycInReview.value) {
    return PillState.KycInReview;
  }
  return PillState.Active;
});

const trackPillClick = () => {
  Analytics.track(UserClick({ component: 'credit_limit_pill', action: 'click', pill_state: pillState.value }));
};

// No dedup: impressions must recur per page nav for correct CTR-by-state math.
// Gated on isVisible so only the currently-visible desktop/mobile instance tracks (see Props).
watch(
  pillState,
  (state) => {
    if (props.isVisible) {
      Analytics.track(CreditLimitPillViewedEvent(state));
    }
  },
  { immediate: true }
);
</script>
